// Contrat des conseils municipaux et départementaux (M16, lot L5-B, H6-02, D-222, D-223, D-240 à D-249) :
// élus et mandats datés, règles versionnées (conseils-regles.ts), sessions (numéro SES) avec ordre du jour,
// convocation, feuille de présence et procurations, quorum recalculé par le serveur, délibérations
// (numéro DEL) et votes nominatifs ou agrégés, procès-verbal PDF validé par le président et le secrétaire
// de séance, transmission (date, mode, référence, accusé), publication explicite et suivi d'exécution.
// Les réponses publiques (portail) ont leurs propres schémas : notes internes et pièces exclues par construction.
import { z } from 'zod';
import { schemaParametresRegle, type EtatQuorum, type ParametresRegle, type TotauxVote } from './conseils-regles';

export * from './conseils-regles';

const uuid = z.string().uuid('Identifiant invalide');
const jour = z.iso.date('Date AAAA-MM-JJ attendue');
const instant = z.iso.datetime({ offset: true, message: 'Date et heure ISO 8601 attendue' });
const texte = (max: number, min = 1) => z.string().trim().min(min).max(max);
const facultatif = (max: number) => z.string().trim().max(max).optional();

export const TYPE_NUMERO_SESSION = 'SES';
export const TYPE_NUMERO_DELIBERATION = 'DEL';

export const FONCTIONS_ELU = { president: 'Maire ou président', adjoint: 'Adjoint ou vice-président', conseiller: 'Conseiller' } as const;
export type FonctionElu = keyof typeof FONCTIONS_ELU;

export const TYPES_SESSION = { ordinaire: 'Session ordinaire', extraordinaire: 'Session extraordinaire' } as const;
export type TypeSession = keyof typeof TYPES_SESSION;

export const ETATS_SESSION = {
  preparee: 'En préparation',
  convoquee: 'Convoquée',
  ouverte: 'Séance ouverte',
  sans_quorum: 'Sans quorum',
  tenue: 'Tenue',
  pv_redige: 'Procès-verbal rédigé',
  pv_valide: 'Procès-verbal validé',
  transmise: 'Transmise',
  publiee: 'Publiée',
  archivee: 'Archivée',
} as const;
export type EtatSession = keyof typeof ETATS_SESSION;

export const ETATS_DELIBERATION = {
  inscrite: 'Inscrite',
  adoptee: 'Adoptée',
  rejetee: 'Rejetée',
  reportee: 'Reportée',
  transmise: 'Transmise',
  publiee: 'Publiée',
  executee: 'Exécutée',
} as const;
export type EtatDeliberation = keyof typeof ETATS_DELIBERATION;

export const STATUTS_PRESENCE = { present: 'Présent', represente: 'Représenté (procuration)', excuse: 'Excusé', absent: 'Absent' } as const;
export type StatutPresence = keyof typeof STATUTS_PRESENCE;

export const CHOIX_VOTE = { pour: 'Pour', contre: 'Contre', abstention: 'Abstention', ne_prend_pas_part: 'Ne prend pas part' } as const;
export type ChoixVote = keyof typeof CHOIX_VOTE;

export const MODES_TRANSMISSION = { depot: 'Dépôt contre récépissé', courrier: 'Courrier recommandé', electronique: 'Voie électronique', autre: 'Autre mode' } as const;
export type ModeTransmission = keyof typeof MODES_TRANSMISSION;

/** Objets dont l'exécution d'une délibération peut être suivie (vérifiés dans la collectivité). */
export const OBJETS_EXECUTION = { projet_travaux: 'Projet de travaux', dossier: 'Dossier', budget: 'Budget' } as const;
export type ObjetExecution = keyof typeof OBJETS_EXECUTION;

// --- Requêtes ---------------------------------------------------------------------------------------

export const schemaCreationMandatElu = z
  .object({
    nom: texte(80, 2),
    prenoms: texte(120, 1),
    fonction: z.enum(['president', 'adjoint', 'conseiller']),
    groupe: facultatif(80),
    debut: jour,
    fin: jour.optional(),
    appartenance_id: uuid.optional(),
  })
  .refine((m) => !m.fin || m.fin >= m.debut, { message: 'La fin du mandat suit son début', path: ['fin'] });
export type CreationMandatElu = z.infer<typeof schemaCreationMandatElu>;

export const schemaFinMandatElu = z.object({ fin: jour, motif: texte(300, 3) });
export type FinMandatElu = z.infer<typeof schemaFinMandatElu>;

export const schemaCreationRegle = z.object({
  libelle: texte(160, 3),
  /** Source administrative de la règle (acte, texte) ; sans elle, la règle ne peut être validée. */
  reference_acte: texte(300, 3),
  fictif: z.boolean().default(false),
  parametres: schemaParametresRegle,
});
export type CreationRegle = z.infer<typeof schemaCreationRegle>;

export const schemaCreationSession = z.object({
  type: z.enum(['ordinaire', 'extraordinaire']),
  intitule: texte(200, 3),
  date_seance: instant,
  lieu: texte(200, 2),
  regle_id: uuid.optional(),
});
export type CreationSession = z.infer<typeof schemaCreationSession>;

export const schemaRegleSession = z.object({ regle_id: uuid });

export const schemaCreationPoint = z.object({
  intitule: texte(200, 3),
  /** Exposé destiné aux élus (pas au portail). */
  expose: facultatif(4000),
  rapporteur: facultatif(120),
  note_interne: facultatif(2000),
  document_ids: z.array(uuid).max(10).optional(),
  /** Point soumis à délibération : crée la délibération inscrite avec son projet de texte public. */
  a_deliberer: z.boolean(),
  texte_public: facultatif(8000),
});
export type CreationPoint = z.infer<typeof schemaCreationPoint>;

export const schemaTexteDeliberation = z.object({ objet: texte(200, 3).optional(), texte_public: texte(8000, 10).optional(), note_interne: facultatif(2000) });
export type TexteDeliberation = z.infer<typeof schemaTexteDeliberation>;

export const schemaFeuillePresence = z.object({
  presences: z
    .array(z.object({ mandat_id: uuid, statut: z.enum(['present', 'represente', 'excuse', 'absent']), mandataire_mandat_id: uuid.optional() }))
    .min(1)
    .max(200),
});
export type FeuillePresence = z.infer<typeof schemaFeuillePresence>;

/** Vote nominatif (choix de chaque votant) ou agrégé (totaux), selon le mode imposé par la règle de la session. */
export const schemaVote = z
  .object({
    mode: z.enum(['nominatif', 'agrege']),
    votes: z.array(z.object({ mandat_id: uuid, choix: z.enum(['pour', 'contre', 'abstention', 'ne_prend_pas_part']) })).max(200).optional(),
    pour: z.number().int().min(0).optional(),
    contre: z.number().int().min(0).optional(),
    abstention: z.number().int().min(0).optional(),
    ne_prend_pas_part: z.number().int().min(0).optional(),
  })
  .refine((v) => (v.mode === 'nominatif' ? (v.votes?.length ?? 0) > 0 : v.pour !== undefined && v.contre !== undefined && v.abstention !== undefined), {
    message: 'Vote nominatif : la liste des votes ; vote agrégé : les totaux pour, contre et abstention',
    path: ['mode'],
  });
export type Vote = z.infer<typeof schemaVote>;

export const schemaReport = z.object({ motif: texte(300, 3) });
export const schemaMotifSession = z.object({ motif: facultatif(300) });
export const schemaValidationPv = z.object({ qualite: z.enum(['president', 'secretaire']) });
export const schemaBureauSeance = z.object({ president_mandat_id: uuid, secretaire_mandat_id: uuid });

export const schemaTransmission = z.object({
  date: jour,
  mode: z.enum(['depot', 'courrier', 'electronique', 'autre']),
  reference: texte(120, 2),
  destinataire: texte(160, 2),
});
export type Transmission = z.infer<typeof schemaTransmission>;

export const schemaAccuse = z.object({ date: jour, reference: facultatif(120), document_id: uuid.optional() });
export type Accuse = z.infer<typeof schemaAccuse>;

export const schemaLienExecution = z.object({ objet_type: z.enum(['projet_travaux', 'dossier', 'budget']), objet_id: uuid });
export const schemaExecution = z.object({ observation: texte(1000, 3) });
export const schemaFiltreSessions = z.object({ etat: z.string().regex(/^[a-z_]+$/).optional(), limite: z.coerce.number().int().min(1).max(500).default(100) });

// --- Réponses de l'administration -------------------------------------------------------------------

export interface MandatElu {
  id: string;
  nom: string;
  prenoms: string;
  nom_complet: string;
  fonction: FonctionElu;
  groupe: string | null;
  debut: string;
  fin: string | null;
  motif_fin: string | null;
  en_exercice: boolean;
  compte: { appartenance_id: string; nom: string | null } | null;
  fictif: boolean;
}

export interface RegleConseil {
  id: string;
  version: number;
  libelle: string;
  reference_acte: string;
  fictif: boolean;
  parametres: ParametresRegle;
  validee_le: string | null;
  validee_par: string | null;
  cree_le: string;
}

export interface ResumeSession {
  id: string;
  numero: string;
  type: TypeSession;
  intitule: string;
  date_seance: string;
  lieu: string;
  etat: EtatSession;
  regle: { id: string; version: number; libelle: string; fictif: boolean } | null;
  quorum: EtatQuorum | null;
  deliberations: number;
  fictif: boolean;
}

export interface ObjetLie {
  type: ObjetExecution;
  id: string;
  /** Faux si le lecteur n'a pas le droit de voir l'objet : seul le type est montré. */
  visible: boolean;
  libelle: string | null;
  etat: string | null;
  suivi: string | null;
}

export interface DeliberationAdmin {
  id: string;
  numero: string | null;
  point_id: string;
  objet: string;
  texte_public: string | null;
  note_interne: string | null;
  etat: EtatDeliberation;
  mode_vote: 'nominatif' | 'agrege' | null;
  totaux: TotauxVote | null;
  votes: { mandat_id: string; nom_complet: string; choix: string; par_procuration: boolean }[];
  quorum_constate: { requis: number; compte: number } | null;
  motif_report: string | null;
  votee_le: string | null;
  publiee_le: string | null;
  objet_lie: ObjetLie | null;
  execution: { executee_le: string; observation: string } | null;
}

export interface MembreSession {
  mandat_id: string;
  nom_complet: string;
  fonction: FonctionElu;
  statut: StatutPresence | null;
  mandataire: { mandat_id: string; nom_complet: string } | null;
}

export interface PointOrdreJour {
  id: string;
  rang: number;
  intitule: string;
  expose: string | null;
  rapporteur: string | null;
  note_interne: string | null;
  documents: { id: string; titre: string }[];
  deliberation: DeliberationAdmin | null;
}

export interface SessionConseil extends ResumeSession {
  regle_parametres: ParametresRegle | null;
  convoquee_le: string | null;
  date_limite_convocation: string | null;
  membres: MembreSession[];
  points: PointOrdreJour[];
  bureau: { president: { mandat_id: string; nom_complet: string } | null; secretaire: { mandat_id: string; nom_complet: string } | null };
  pv: {
    document_id: string;
    validations: { qualite: 'president' | 'secretaire'; par: string | null; le: string }[];
  } | null;
  transmission: { date: string; mode: ModeTransmission; reference: string; destinataire: string; accuse: { date: string; reference: string | null; document_id: string | null } | null } | null;
  publiee_le: string | null;
  transitions: { code: string; libelle: string; vers: string }[];
  /** Le lecteur gère le secrétariat (notes internes visibles). */
  gestion: boolean;
}

// --- Réponses publiques (portail, sans compte) -----------------------------------------------------

export const schemaDeliberationPublique = z.object({
  id: z.string().uuid(),
  numero: z.string(),
  objet: z.string(),
  texte_public: z.string(),
  totaux: z.object({ pour: z.number(), contre: z.number(), abstention: z.number() }),
  date_seance: z.string(),
  session: z.object({ numero: z.string(), type: z.enum(['ordinaire', 'extraordinaire']), intitule: z.string() }),
  publiee_le: z.string(),
  execution: z.object({
    etat: z.enum(['en_cours', 'executee']),
    objet: z.object({ type: z.enum(['projet_travaux', 'dossier', 'budget']), libelle: z.string(), etat: z.string() }).nullable(),
  }),
  fictif: z.boolean(),
}).strict();
export type DeliberationPublique = z.infer<typeof schemaDeliberationPublique>;
